import React, { useState } from 'react';
import axios from 'axios';
import { FaCalculator, FaSpinner, FaInfoCircle, FaBuilding, FaRuler, FaTools, FaClock, FaDollarSign } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { materialsByProjectType } from './projectMaterials';

const EstimateForm = () => {
  const [estimate, setEstimate] = useState({
    projectType: '',
    squareFootage: 0,
    materials: [],
    laborHours: 0,
    additionalCosts: 0,
  });
  const [materialOptions, setMaterialOptions] = useState([]);
  const [generatedEstimate, setGeneratedEstimate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, selectedOptions } = e.target;

    if (name === 'projectType') {
      setMaterialOptions(materialsByProjectType[value] || []);
      setEstimate({ ...estimate, projectType: value, materials: [] }); // Reset materials when project type changes
    } else if (name === 'materials') {
      const selectedMaterials = Array.from(selectedOptions, option => option.value);
      setEstimate({ ...estimate, materials: selectedMaterials });
    } else {
      setEstimate({ ...estimate, [name]: value });
    }
  };

  const generateEstimate = async () => {
    if (!estimate.projectType || !estimate.squareFootage || !estimate.materials.length || !estimate.laborHours) {
      toast.error('Por favor, completa todos los campos obligatorios');
      return;
    }
    setIsLoading(true);
    try {
      const response = await axios.post('https://api.openai.com/v1/chat/completions', {
        model: "gpt-3.5-turbo",
        messages: [{
          role: "system",
          content: "You are a helpful assistant that generates detailed construction estimates."
        }, {
          role: "user",
          content: `Generate a detailed estimate for a ${estimate.projectType} project with ${estimate.squareFootage} square feet, using ${estimate.materials.join(', ')} as main materials, ${estimate.laborHours} labor hours, and $${estimate.additionalCosts} in additional costs. Include a breakdown of costs, timeline, and potential risks.`
        }],
      }, {
        headers: {
          'Authorization': `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        }
      });
      setGeneratedEstimate(response.data.choices[0].message.content);
      toast.success('Estimación generada con éxito');
    } catch (error) {
      console.error('Error generating estimate:', error);
      toast.error('Error al generar la estimación');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-2xl mx-auto">
      <ToastContainer />

      {/* Image with Overlay Text */}
      <div className="relative mb-6">
        <img src="/images/2.jpeg" alt="Project Estimate" className="w-full h-48 object-cover rounded-lg shadow-lg" />
        <div className="absolute inset-0 bg-black bg-opacity-50 flex justify-center items-center">
          <h2 className="text-3xl font-bold text-white">
            Obtén tu Estimación Detallada
          </h2>
        </div>
      </div>

      <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
        <FaCalculator className="mr-2 text-blue-500" />
        Formulario de Estimación
      </h2>

      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="projectType">
          <FaBuilding className="inline mr-2" /> Tipo de Proyecto
        </label>
        <select 
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          name="projectType" 
          onChange={handleInputChange}
          value={estimate.projectType}
        >
          <option value="">Selecciona el tipo de proyecto</option>
          <option value="renovation">Renovación</option>
          <option value="newConstruction">Nueva Construcción</option>
          <option value="addition">Ampliación</option>
          <option value="remodel">Remodelación</option>
          <option value="roofing">Techos</option>
          <option value="flooring">Pisos</option>
          <option value="painting">Pintura</option>
          <option value="landscaping">Paisajismo</option>
          <option value="plumbing">Plomería</option>
          <option value="electrical">Electricidad</option>
          <option value="hvac">HVAC</option>
          <option value="solarInstallation">Instalación Solar</option>
          <option value="fencing">Cercado</option>
          <option value="decking">Decks</option>
          <option value="kitchenRemodel">Remodelación de Cocina</option>
          <option value="bathroomRemodel">Remodelación de Baño</option>
          <option value="basementFinishing">Terminación de Sótano</option>
          <option value="windowReplacement">Reemplazo de Ventanas</option>
          <option value="sidingInstallation">Instalación de Revestimiento</option>
          <option value="garageConstruction">Construcción de Garaje</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="materials">
          <FaTools className="inline mr-2" /> Materiales Principales
        </label>
        <select 
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          name="materials" 
          onChange={handleInputChange}
          value={estimate.materials}
          multiple // Permitir selección múltiple
          disabled={!materialOptions.length} // Desactivar si no hay materiales disponibles
        >
          <option value="">Selecciona los materiales</option>
          {materialOptions.map((material, index) => (
            <option key={index} value={material}>{material}</option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="squareFootage">
          <FaRuler className="inline mr-2" /> Metros Cuadrados
        </label>
        <input
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          type="number"
          name="squareFootage"
          placeholder="Metros cuadrados"
          onChange={handleInputChange}
          value={estimate.squareFootage}
        />
      </div>

      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="laborHours">
          <FaClock className="inline mr-2" /> Horas de Trabajo Estimadas
        </label>
        <input
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          type="number"
          name="laborHours"
          placeholder="Horas de trabajo estimadas"
          onChange={handleInputChange}
          value={estimate.laborHours}
        />
      </div>

      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="additionalCosts">
          <FaDollarSign className="inline mr-2" /> Costos Adicionales
        </label>
        <input
          className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          type="number"
          name="additionalCosts"
          placeholder="Costos adicionales"
          onChange={handleInputChange}
          value={estimate.additionalCosts}
        />
      </div>

      <button 
        className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition duration-300 flex items-center justify-center"
        onClick={generateEstimate}
        disabled={isLoading}
      >
        {isLoading ? <FaSpinner className="animate-spin mr-2" /> : <FaCalculator className="mr-2" />}
        {isLoading ? 'Generando...' : 'Generar Estimación'}
      </button>

      {generatedEstimate && (
        <div className="mt-6 p-4 border rounded-lg bg-gray-50">
          <h3 className="font-bold mb-2 text-lg flex items-center">
            <FaInfoCircle className="mr-2 text-blue-500" /> Estimación Generada:
          </h3>
          <p className="whitespace-pre-wrap">{generatedEstimate}</p>
        </div>
      )}
    </div>
  );
};

export default EstimateForm;
